import { h, Component, ComponentChildren } from "preact";
import accepts from "attr-accept";

export default class Dropzone extends Component<
  IDropzoneProps,
  IDropzoneState
> {
  fileInputEl: HTMLInputElement;
  isFileDialogActive: boolean;
  enterCounter: number;

  constructor(props: IDropzoneProps) {
    super(props);

    this.state = {
      isDragActive: false,
      isDragReject: false,
    };

    this.onClick = this.onClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onFileDialogCancel = this.onFileDialogCancel.bind(this);
    this.fileAccepted = this.fileAccepted.bind(this);
    this.isFileDialogActive = false;
  }

  componentDidMount(): void {
    this.enterCounter = 0;
    // Tried implementing addEventListener, but didn't work out
    document.body.onfocus = this.onFileDialogCancel;

    if (this.props.bind) {
      this.props.bind(this);
    }
  }

  componentWillUnmount(): void {
    // Can be replaced with removeEventListener, if addEventListener works
    document.body.onfocus = null;
  }

  onDragStart(e: any): void {
    if (this.props.onDragStart) {
      this.props.onDragStart.call(this, e);
    }
  }

  onDragEnter(e: any): void {
    e.preventDefault();

    // Count the dropzone and any children that are entered.
    ++this.enterCounter;

    // This is tricky. During the drag even the dataTransfer.files is null
    // But Chrome implements some drag store, which is accesible via dataTransfer.items
    const dataTransferItems: Array<any> =
      e.dataTransfer && e.dataTransfer.items ? e.dataTransfer.items : [];

    // Now we need to convert the DataTransferList to Array
    const allFilesAccepted: boolean = this.allFilesAccepted(
      Array.prototype.slice.call(dataTransferItems),
    );

    this.setState({
      isDragActive: allFilesAccepted,
      isDragReject: !allFilesAccepted,
    });

    if (this.props.onDragEnter) {
      this.props.onDragEnter.call(this, e);
    }
  }

  onDragOver(e: any): boolean {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy"; // eslint-disable-line no-param-reassign
    return false;
  }

  onDragLeave(e: any): void {
    e.preventDefault();

    // Only deactivate once the dropzone and all children was left.
    if (--this.enterCounter > 0) {
      return;
    }

    this.setState({
      isDragActive: false,
      isDragReject: false,
    });

    if (this.props.onDragLeave) {
      this.props.onDragLeave.call(this, e);
    }
  }

  onDrop(e: any): void {
    e.preventDefault();

    // Reset the counter along with the drag on a drop.
    this.enterCounter = 0;

    this.setState({
      isDragActive: false,
      isDragReject: false,
    });

    const droppedFiles: FileList = e.dataTransfer
      ? e.dataTransfer.files
      : e.target.files;
    const max: number = this.props.multiple
      ? droppedFiles.length
      : Math.min(droppedFiles.length, 1);
    const acceptedFiles: Array<File> = [];
    const rejectedFiles: Array<File> = [];

    for (let i: number = 0; i < max; i++) {
      const file: any = droppedFiles[i];
      // We might want to disable the preview creation to support big files
      if (!this.props.disablePreview) {
        file.preview = window.URL.createObjectURL(file);
      }
      if (this.fileAccepted(file) && this.fileMatchSize(file)) {
        acceptedFiles.push(file);
      } else {
        rejectedFiles.push(file);
      }
    }

    if (this.props.onDrop) {
      this.props.onDrop.call(this, acceptedFiles, rejectedFiles, e);
    }

    if (rejectedFiles.length > 0) {
      if (this.props.onDropRejected) {
        this.props.onDropRejected.call(this, rejectedFiles, e);
      }
    } else if (acceptedFiles.length > 0) {
      if (this.props.onDropAccepted) {
        this.props.onDropAccepted.call(this, acceptedFiles, e);
      }
    }
    this.isFileDialogActive = false;
  }

  onClick(): void {
    if (!this.props.disableClick) {
      this.open();
    }
  }

  onFileDialogCancel(): void {
    // execute the timeout only if the onFileDialogCancel is defined and FileDialog
    // is opened in the browser
    if (this.props.onFileDialogCancel && this.isFileDialogActive) {
      setTimeout(() => {
        // Returns an object as FileList
        const fileList: FileList = this.fileInputEl.files;
        if (!fileList.length) {
          this.isFileDialogActive = false;
          this.props.onFileDialogCancel();
        }
      }, 300);
    }
  }

  fileAccepted(file: File): boolean {
    return accepts(file, this.props.accept);
  }

  fileMatchSize(file: File): boolean {
    return file.size <= this.props.maxSize && file.size >= this.props.minSize;
  }

  allFilesAccepted(files: Array<File>): boolean {
    return files.every(this.fileAccepted);
  }

  open(): void {
    this.isFileDialogActive = true;
    this.fileInputEl.value = null;
    this.fileInputEl.click();
  }

  render(): JSX.Element {
    let className: string = this.props.className || "";

    if (this.state.isDragActive && this.props.activeClassName) {
      className += " " + this.props.activeClassName;
    }
    if (this.state.isDragReject && this.props.rejectClassName) {
      className += " " + this.props.rejectClassName;
    }

    const inputAttributes: any = {
      accept: this.props.accept,
      type: "file",
      style: { display: "none" },
      multiple: this.props.multiple,
      ref: (el: HTMLInputElement) => {
        this.fileInputEl = el;
      }, // eslint-disable-line
      onChange: this.onDrop,
    };

    if (this.props.name && this.props.name.length) {
      inputAttributes.name = this.props.name;
    }

    // Remove custom properties before passing them to the wrapper div element
    const customProps: Array<string> = [
      "disablePreview",
      "disableClick",
      "onDropAccepted",
      "onDropRejected",
      "maxSize",
      "minSize",
      "onFileDialogCancel",
    ];
    const divProps: any = { ...this.props };
    customProps.forEach((prop: string) => {
      delete divProps[prop];
    });

    return (
      <div
        className={className}
        {
          ...divProps /* expand user provided props first so event handlers are never overridden */
        }
        onClick={this.onClick}
        onDragStart={this.onDragStart}
        onDragEnter={this.onDragEnter}
        onDragOver={this.onDragOver}
        onDragLeave={this.onDragLeave}
        onDrop={this.onDrop}
      >
        {this.props.children}
        <input
          {
            ...this.props
              .inputProps /* expand user provided inputProps first so inputAttributes override them */
          }
          {...inputAttributes}
        />
      </div>
    );
  }
}

Dropzone.defaultProps = {
  disablePreview: false,
  disableClick: false,
  multiple: true,
  maxSize: Infinity,
  minSize: 0,
  activeClassName: "active",
  rejectClassName: "reject",
};

interface IDropzoneProps {
  // Overriding drop behavior
  onDrop?: Function;
  onDropAccepted?: Function;
  onDropRejected?: Function;
  // Overriding drag behavior
  onDragStart?: Function;
  onDragEnter?: Function;
  onDragLeave?: Function;
  style?: object;
  activeStyle?: object;
  rejectStyle?: object;
  className?: string;
  activeClassName?: string;
  rejectClassName?: string;
  disablePreview?: boolean;
  disableClick?: boolean;
  onFileDialogCancel?: Function;
  inputProps?: any;
  multiple?: boolean;
  accept?: string;
  name?: string;
  maxSize?: number;
  minSize?: number;
  bind?: Function;
  children?: ComponentChildren;
}

interface IDropzoneState {
  isDragActive: boolean;
  isDragReject: boolean;
}
